/**
 * @fileoverview Menú contextual de mensaje (átomo) para acciones rápidas sobre mensajes en el chat.
 * Encierra el botón en un círculo y lo muestra solo al hacer hover sobre el mensaje.
 * Cumple Atomic Design y separación estricta de UI Layer.
 *
 * @module MessageMenuMC
 * @category Component - Atom
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, hover y círculo visual
 *
 * @typedef {Object} MessageMenuMCProps
 * @property {Object} message - Objeto mensaje individual (ver typedef Message en AreaChatWindowMC)
 * @property {Object} conversation - Objeto conversación activa
 *
 * @component MessageMenuMC
 * @category Component - Atom
 *
 * @description
 * **Características:**
 * - Menú contextual para acciones rápidas (ej: Responder)
 * - Botón encerrado en círculo, visible solo en hover
 * - Posicionamiento absoluto en esquina superior derecha del mensaje
 * - Cumple Atomic Design: Átomo, reutilizable en cualquier tipo de mensaje
 * - Integración con DropdownButtonMC
 *
 * **Atomic Design:**
 * - **Nivel:** Atom
 * - **Composición:** Usa DropdownButtonMC
 * - **Dependencias:** Redux, useChatAvailability
 *
 * **Props Interface:**
 * ```javascript
 * {
 *   message: Object,        // Mensaje individual
 *   conversation: Object    // Conversación activa
 * }
 * ```
 *
 * @param {MessageMenuMCProps} props - Propiedades del componente
 * @returns {JSX.Element|null} Menú contextual renderizado o null si no aplica
 *
 * @example
 * <MessageMenuMC message={message} conversation={conversation} />
 *
 * @see {@link ../../../../components/DropdownButtonMC.js} Componente de dropdown
 * @see {@link ../../AreaChatWindowMC.js} Componente padre
 *
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, hover y círculo visual
 */
import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useChatAvailability from '../../../hooks/inbox/useChatAvailability';
import DropdownButton from '../../../components/DropdownButtonMC';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import canReplyToMessage from '../utils/messageMenuVisibility';

const MessageMenuMC = memo(({ message, conversation }) => {
  const { gs_integrated, is_capi } = useSelector(
    (reduxState) => reduxState.retailerUsersReducer.retailer_info
  );
  const { isChatClosed } = useChatAvailability({ conversation });

  const handleMenuSelect = useCallback(() => {
    sharingInformationService.setSubject({
      selectMessageToReply: true,
      message
    });
    sharingInformationService.setSubject({});
  }, [message]);

  const menuOptions = useMemo(() => [
    {
      label: 'Responder',
      value: 'chat',
      onClick: handleMenuSelect
    }
  ], [handleMenuSelect]);

  if (isChatClosed) return null;
  if (message.isNote || message.isOrderNote) return null;
  if (!canReplyToMessage({
    platform: conversation?.platform,
    canReply: message.canReply,
    gsIntegrated: gs_integrated,
    isCapi: is_capi
  })) return null;

  return (
    <div className="tw-absolute tw-top-1 tw-right-1 tw-z-30 tw-opacity-0 tw-transition-opacity tw-duration-200 group-hover:tw-opacity-100">
      <div className="tw-bg-white tw-rounded-full tw-shadow tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 hover:tw-bg-blue-50">
        <DropdownButton
          variant="TEXT_PLAIN"
          items={menuOptions}
          onSelect={handleMenuSelect}
          dropdownClassName="tw-min-w-[200px] tw-max-w-[400px] tw-right-0"
          className="!tw-text-blue-500 !tw-h-4 !tw-p-0"
        />
      </div>
    </div>
  );
});

MessageMenuMC.displayName = 'MessageMenuMC';

export default MessageMenuMC;
